import {
  Children,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useIsDesktopView } from "../../common/hooks/useIsDesktopView";
import { cn } from "../../lib/utils";

// Horizontal/vertical inset applied to each slide. `sm` is the default small
// gutter used by most carousels; `page` makes a full-bleed carousel whose slide
// content lines up with the page's horizontal padding (px-5) and has no vertical
// padding; `wide` is the 22px side inset of the rooms content block.
const SLIDE_GUTTER = {
  sm: "p-2",
  page: "px-5",
  wide: "px-5.5",
} as const satisfies Record<string, string>;

type SlideGutter = keyof typeof SLIDE_GUTTER;

// Share of a slide that must be visible for the slide to become the active one.
const ACTIVE_SLIDE_RATIO = 0.6;

export type AppCarouselHandle = {
  scrollToIndex: (index: number, behavior: ScrollBehavior) => void;
};

/**
 * Horizontal carousel that shows one slide at a time. Native scroll snapping
 * moves one slide per swipe; an IntersectionObserver reports the active slide.
 */
function AppCarousel(
  {
    children,
    slideGutter,
    swipeable = true,
    displayPagination = true,
    initialSlide = 0,
    desktopGrid = false,
    onIndexChange = () => null,
  }: {
    children: ReactNode[];
    slideGutter: SlideGutter;
    swipeable?: boolean;
    displayPagination?: boolean;
    // Read once, at mount.
    initialSlide?: number;
    desktopGrid?: boolean;
    // Called for each slide that becomes active, so a smooth jump across
    // several slides reports every slide it passes.
    onIndexChange?: (index: number) => void;
  },
  ref: ForwardedRef<AppCarouselHandle>
) {
  const { isRtl } = useActiveLang();
  const { isDesktop } = useIsDesktopView();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(initialSlide);
  const activeIndexRef = useRef(initialSlide);
  const onIndexChangeRef = useRef(onIndexChange);
  useLayoutEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  });
  const slides = Children.toArray(children);
  const isCarousel = !(desktopGrid && isDesktop);

  function scrollToIndex(index: number, behavior: ScrollBehavior) {
    const container = containerRef.current;
    if (container === null)
      throw new Error("should not happen: carousel is not mounted");
    // Every slide fills the carousel, so slide N starts N widths away. In RTL
    // the scroll offset grows towards negative values. Past the last slide
    // the browser clamps the scroll: nothing moves.
    const left = index * container.clientWidth * (isRtl ? -1 : 1);
    container.scrollTo({ left, behavior });
  }

  useImperativeHandle(ref, () => ({ scrollToIndex }));

  // Before paint, so the carousel never shows another slide first. On mount
  // the active slide is the initial one. A change of text direction moves the
  // scroll origin to the other side, so the carousel scrolls back to the
  // active slide. Otherwise the reader's own scroll moves the carousel.
  useLayoutEffect(() => {
    if (!isCarousel) return;
    scrollToIndex(activeIndexRef.current, "instant");
  }, [isCarousel, isRtl]);

  useEffect(() => {
    if (!isCarousel) return;
    const container = containerRef.current;
    if (container === null)
      throw new Error("should not happen: carousel is not mounted");
    const slideElements = Array.from(container.children);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio < ACTIVE_SLIDE_RATIO) continue;
          const index = slideElements.indexOf(entry.target);
          if (index === -1)
            throw new Error("should not happen: observed a foreign slide");
          if (index === activeIndexRef.current) continue;
          activeIndexRef.current = index;
          setActiveIndex(index);
          onIndexChangeRef.current(index);
        }
      },
      { root: container, threshold: ACTIVE_SLIDE_RATIO }
    );
    for (const slide of slideElements) observer.observe(slide);
    return () => {
      observer.disconnect();
    };
  }, [isCarousel, slides.length]);

  // A rotation or a window resize changes the slide width. Chromium re-snaps
  // to the same slide by itself; this keeps every engine on the active slide.
  useEffect(() => {
    if (!isCarousel) return;
    const container = containerRef.current;
    if (container === null)
      throw new Error("should not happen: carousel is not mounted");
    let width = container.clientWidth;
    const observer = new ResizeObserver(() => {
      if (container.clientWidth === width) return;
      width = container.clientWidth;
      scrollToIndex(activeIndexRef.current, "instant");
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [isCarousel, isRtl]);

  if (!isCarousel) {
    return (
      <div className="@container px-2">
        <div className="grid grid-cols-2 gap-3">{slides}</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        dir={isRtl ? "rtl" : "ltr"}
        className={cn(
          "flex w-full overscroll-x-contain snap-x snap-mandatory scrollbar-none [&::-webkit-scrollbar]:hidden",
          swipeable ? "overflow-x-auto" : "overflow-x-hidden"
        )}
        onTouchMove={(e) => {
          // A swipe in the carousel is not a swipe of the page around it.
          if (!isDesktop) e.stopPropagation();
        }}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            className={cn(
              "box-border flex min-w-0 shrink-0 grow-0 basis-full snap-start snap-always *:min-w-0 *:flex-1",
              SLIDE_GUTTER[slideGutter]
            )}
          >
            {slide}
          </div>
        ))}
      </div>
      {displayPagination && (
        <div className="mt-2 flex items-center justify-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`${index + 1} / ${slides.length}`}
              aria-current={index === activeIndex}
              className={cn(
                "cursor-pointer rounded-full border-0 p-0 [transition:width_0.2s_ease]",
                isDesktop ? "h-3" : "h-2",
                index === activeIndex
                  ? "w-8 bg-black"
                  : cn("bg-[#d9d9d9]", isDesktop ? "w-3" : "w-2")
              )}
              onClick={(e) => {
                // A dot moves the carousel; it does not activate the slide.
                e.stopPropagation();
                scrollToIndex(index, "smooth");
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default forwardRef(AppCarousel);
